import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector } from '@common-reducers/UserSelectors';
import { useGetMediaTripStateMutation, useRequestMediaReloadMutation } from '@common-reducers/backend-api/backend-api';
import { isWhatsappExtensionContext } from '@common-services/detect-context';
import {
  MEDIA_TRIP_POLL_MS,
  reloadOutcomeFrom,
  shouldShowMediaTripBanner,
  type MediaReloadOutcome,
} from './media-trip-logic';

/**
 * Shown while WhatsApp is rejecting media from this account (the backend media breaker is tripped). Hidden as soon as
 * one send confirms. The Reload button reloads WhatsApp Web: directly when running inside the WhatsApp tab, otherwise
 * through the extension (which never reloads a tab the user is using).
 */
const MediaTripBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isDunning } = useAppSelector(dunningSelector);
  const [readTrip] = useGetMediaTripStateMutation();
  const [requestReload] = useRequestMediaReloadMutation();
  const [tripped, setTripped] = useState(false);
  const [outcome, setOutcome] = useState<MediaReloadOutcome>('idle');

  useEffect(() => {
    let live = true;
    const poll = async (): Promise<void> => {
      try {
        const reply = await readTrip().unwrap();
        if (live) setTripped(reply.ok && reply.tripped);
      } catch {
        if (live) setTripped(false);
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), MEDIA_TRIP_POLL_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [readTrip]);

  const onReload = useCallback(async (): Promise<void> => {
    if (isWhatsappExtensionContext()) {
      window.location.reload();
      return;
    }
    setOutcome('sending');
    try {
      setOutcome(reloadOutcomeFrom(await requestReload().unwrap()));
    } catch {
      setOutcome('failed');
    }
  }, [requestReload]);

  if (!shouldShowMediaTripBanner(tripped, !!isDunning)) return null;

  const note = outcome === 'sent' ? t('mediaTripBanner.sent') : outcome === 'failed' ? t('mediaTripBanner.failed') : t('mediaTripBanner.body');

  return (
    <div
      role="alert"
      style={{
        width: '100%',
        backgroundColor: '#b45309',
        color: '#fff',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        fontSize: '14px',
        fontWeight: 500,
        boxSizing: 'border-box',
      }}
    >
      <span>
        <strong>{t('mediaTripBanner.headline')}</strong>
        <span style={{ marginInlineStart: 8, fontWeight: 400 }}>{note}</span>
      </span>
      <button
        disabled={outcome === 'sending' || outcome === 'sent'}
        onClick={() => void onReload()}
        style={{
          backgroundColor: '#fff',
          color: '#b45309',
          border: 'none',
          borderRadius: '4px',
          padding: '6px 16px',
          fontWeight: 600,
          cursor: outcome === 'sending' || outcome === 'sent' ? 'default' : 'pointer',
          fontSize: '13px',
          whiteSpace: 'nowrap',
        }}
      >
        {t('mediaTripBanner.cta')}
      </button>
    </div>
  );
};

export default MediaTripBanner;
